<template>
  <div class="w-full text-xs px-2.5 py-2" :class="tintClass">
    <!-- equal side slots so the teams and score sit in the middle -->
    <button
      type="button"
      class="w-full flex items-center gap-2"
      :aria-expanded="String(isOpen)"
      v-on:click="toggleOpen"
    >
      <!-- balances the chevron so the score sits in the middle; the background tint says W/D/L -->
      <span class="w-6 flex-none" aria-hidden="true"></span>
      <span class="flex-1 min-w-0 flex items-center justify-end gap-1.5">
        <span class="truncate text-right" :class="{ 'font-semibold': isFocus(d.home_team) }"><span class="hidden sm:inline">{{ d.home_team.name }}</span><span class="sm:hidden">{{ d.home_team.short_name || d.home_team.name }}</span></span>
        <img v-if="d.home_team.logo" class="cursor-pointer w-5 h-5 object-contain flex-none" :src="d.home_team.logo" alt="" @click.stop="openTeam(d.home_team)" title="Team page" />
      </span>
      <span class="flex-none px-2 py-1 rounded bg-gray-800/10 font-semibold tabular-nums text-gray-800">{{ d.result || "–" }}</span>
      <span class="flex-1 min-w-0 flex items-center gap-1.5">
        <img v-if="d.away_team.logo" class="cursor-pointer w-5 h-5 object-contain flex-none" :src="d.away_team.logo" alt="" @click.stop="openTeam(d.away_team)" title="Team page" />
        <span class="truncate text-left" :class="{ 'font-semibold': isFocus(d.away_team) }"><span class="hidden sm:inline">{{ d.away_team.name }}</span><span class="sm:hidden">{{ d.away_team.short_name || d.away_team.name }}</span></span>
      </span>
      <span class="w-6 flex-none flex justify-center">
        <svg
          class="w-3 h-3 text-blue-700 transform transition-transform"
          :class="{ 'rotate-180': isOpen }"
          viewBox="0 0 10 6"
          aria-hidden="true"
        >
          <path d="M0 0.5L5 5.5L10 0.5Z" fill="currentColor" />
        </svg>
      </span>
    </button>

    <div v-if="isOpen" class="mt-3 pt-3 border-t border-gray-200 grid gap-4">
      <p class="text-center text-gray-500">{{ longDate }}<template v-if="d.competition"> · {{ d.competition }}</template></p>
      <p v-if="!hasDetail" class="text-center text-gray-500">
        No match details from our data provider for this game.
      </p>

      <!-- expected goals as a second scoreline: home left, away right, like the result above -->
      <div v-if="hasXg" class="flex flex-col items-center gap-1.5">
        <span class="text-[10px] uppercase tracking-wide text-gray-500">Expected goals (xG)</span>
        <span class="flex items-center gap-2 tabular-nums">
          <span class="min-w-[3rem] text-center rounded-md bg-white border-b-[3px] border-[#E59C98] shadow-sm px-2.5 py-1 text-sm font-semibold text-gray-800">{{
            fmt1(d.home_team.xg_for)
          }}</span>
          <span class="text-gray-400">–</span>
          <span class="min-w-[3rem] text-center rounded-md bg-white border-b-[3px] border-[#BDBDFB] shadow-sm px-2.5 py-1 text-sm font-semibold text-gray-800">{{
            fmt1(d.away_team.xg_for)
          }}</span>
        </span>
      </div>

      <!-- goals and cards, home left / away right -->
      <div v-if="timeline.length" class="grid gap-2 text-gray-700">
        <div v-for="(e, i) in timeline" :key="i" class="grid items-center gap-3 timeline-row">
          <span class="text-right break-words leading-tight" :class="{ invisible: !e.home }">
            <template v-if="e.home">{{ e.label }}</template>
          </span>
          <span class="flex items-center justify-center gap-1 text-gray-500 tabular-nums">
            <img class="w-3 h-3" :src="e.icon" alt="" />{{ e.minute }}
          </span>
          <span class="break-words leading-tight" :class="{ invisible: e.home }">
            <template v-if="!e.home">{{ e.label }}</template>
          </span>
        </div>
      </div>

      <!-- team stat bars -->
      <div v-if="statRows.length" class="grid gap-2">
        <div v-for="row in statRows" :key="row.label">
          <div class="flex justify-between text-gray-500 mb-0.5 tabular-nums">
            <b class="text-gray-800">{{ row.home }}{{ row.unit }}</b>
            <span>{{ row.label }}</span>
            <b class="text-gray-800">{{ row.away }}{{ row.unit }}</b>
          </div>
          <div class="flex h-1.5 gap-0.5" aria-hidden="true">
            <span class="flex justify-end flex-1 bg-gray-200 rounded-l">
              <span class="block h-full rounded-l bg-[#E59C98]" :style="{ width: row.homePct + '%' }"></span>
            </span>
            <span class="flex-1 bg-gray-200 rounded-r">
              <span class="block h-full rounded-r bg-[#BDBDFB]" :style="{ width: row.awayPct + '%' }"></span>
            </span>
          </div>
        </div>
      </div>

      <!-- our pre-match prediction -->
      <div v-if="d.home_prediction" class="grid gap-1">
        <div class="text-center text-gray-500">Our pre-match prediction</div>
        <div class="grid grid-cols-3 gap-1 text-center tabular-nums">
          <span
            v-for="side in predictionSides"
            :key="side.key"
            class="rounded py-1"
            :class="side.actual ? 'bg-[#22c55e]/15 text-gray-800 font-semibold' : 'bg-gray-800/5 text-gray-500'"
            >{{ side.label }} {{ Math.round(side.value) }}%<template v-if="side.actual"> ✓</template></span
          >
        </div>
      </div>

      <!-- players: shots (on target), then bookings; no minutes, just who -->
      <div v-if="shooters.home.length || shooters.away.length" class="grid grid-cols-2 gap-4 text-gray-600">
        <div v-for="side in ['home', 'away']" :key="`s-${side}`" :class="{ 'text-right': side === 'away' }">
          <div class="text-[10px] uppercase tracking-wide mb-1 text-gray-500">Shots (on target)</div>
          <div v-for="s in shooters[side]" :key="s.name" class="truncate leading-relaxed">
            <template v-if="side === 'home'"
              ><b class="text-gray-800 tabular-nums">{{ s.shots }} ({{ s.sot }})</b> {{ s.name }}</template
            >
            <template v-else
              >{{ s.name }} <b class="text-gray-800 tabular-nums">{{ s.shots }} ({{ s.sot }})</b></template
            >
          </div>
        </div>
      </div>
      <div v-if="booked.home.length || booked.away.length" class="grid grid-cols-2 gap-4 text-gray-600">
        <div v-for="side in ['home', 'away']" :key="`b-${side}`" :class="{ 'text-right': side === 'away' }">
          <div class="text-[10px] uppercase tracking-wide mb-1 text-gray-500">Booked</div>
          <div
            v-for="b in booked[side]"
            :key="b.name"
            class="flex items-center gap-1.5 leading-relaxed"
            :class="{ 'justify-end': side === 'away' }"
          >
            <img class="w-3 h-3.5 flex-none" :src="b.icon" alt="" :class="{ 'order-last': side === 'away' }" />
            <span class="truncate">{{ b.name }}</span>
          </div>
          <div v-if="!booked[side].length" class="text-gray-400">None</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import goalIcon from "@/assets/icons/goal.png";
import ownGoalIcon from "@/assets/icons/owngoal.png";
import yellowIcon from "@/assets/icons/yellowcard.png";
import redIcon from "@/assets/icons/redcard.png";
import yellowRedIcon from "@/assets/icons/yellowred.png";
import missedPenIcon from "@/assets/icons/missedpenalty.png";

const EVENT_ICONS = {
  goal: goalIcon,
  penalty: goalIcon,
  "own-goal": ownGoalIcon,
  yellowcard: yellowIcon,
  redcard: redIcon,
  yellowred: yellowRedIcon,
  missed_penalty: missedPenIcon,
};
const CARDS = ["yellowcard", "redcard", "yellowred"];
const STATS = [
  ["possession", "Possession", "%"],
  ["shots", "Shots", ""],
  ["shots_on_target", "On target", ""],
  ["corners", "Corners", ""],
  ["fouls", "Fouls", ""],
  ["yellow_cards", "Yellow cards", ""],
];

import teamLinks from "@/teamLinks";
export default {
  mixins: [teamLinks],
  name: "MatchEventListCard",
  props: {
    matchEventDetails: { type: Object, required: true },
    // the team whose form this is: its W/D/L badge and bold name
    teamId: { type: [Number, String], default: null },
    // green / grey / red background for won / drawn / lost (recent form; off for head-to-head)
    tint: { type: Boolean, default: true },
  },
  data() {
    return { isOpen: false };
  },
  computed: {
    d() {
      return this.matchEventDetails;
    },
    longDate() {
      return this.d.date ? dayjs(this.d.date).format("ddd D MMM YYYY") : "";
    },
    goals() {
      const [h, a] = String(this.d.result || "").split("-").map(Number);
      return Number.isFinite(h) && Number.isFinite(a) ? { h, a } : null;
    },
    outcome() {
      if (!this.goals || this.teamId === null) return null;
      const home = String(this.d.home_team.id) === String(this.teamId);
      const away = String(this.d.away_team.id) === String(this.teamId);
      if (!home && !away) return null;
      const { h, a } = this.goals;
      if (h === a) return "D";
      return (home && h > a) || (away && a > h) ? "W" : "L";
    },
    hasXg() {
      return (this.d.home_team.xg_for || 0) > 0 || (this.d.away_team.xg_for || 0) > 0;
    },
    tintClass() {
      if (!this.tint || !this.outcome) return "bg-white";
      return { W: "tint-w", D: "tint-d", L: "tint-l" }[this.outcome];
    },
    timeline() {
      return (this.d.events || [])
        .filter((e) => EVENT_ICONS[e.type] && !CARDS.includes(e.type))
        .map((e) => {
          let label = e.player || "";
          if (e.type === "own-goal") label += " (og)";
          if (e.type === "penalty") label += " (pen)";
          if (e.type === "missed_penalty") label += " (missed pen)";
          return {
            home: String(e.team_id) === String(this.d.home_team.id),
            minute: `${e.minute}'${e.extra_minute ? "+" + e.extra_minute : ""}`,
            sort: (e.minute || 0) + (e.extra_minute || 0) / 100,
            icon: EVENT_ICONS[e.type],
            label,
          };
        })
        .sort((x, y) => x.sort - y.sort);
    },
    statRows() {
      const ts = this.d.team_stats;
      if (!ts || !ts.home_team || !ts.away_team) return [];
      return STATS.filter(([key]) => ts.home_team[key] !== undefined || ts.away_team[key] !== undefined).map(
        ([key, label, unit]) => {
          const home = Number(ts.home_team[key]) || 0;
          const away = Number(ts.away_team[key]) || 0;
          const total = home + away || 1;
          return { label, unit, home, away, homePct: (home / total) * 100, awayPct: (away / total) * 100 };
        },
      );
    },
    predictionSides() {
      const g = this.goals;
      const actual = g ? (g.h > g.a ? "home" : g.h < g.a ? "away" : "draw") : null;
      return [
        { key: "home", label: "Home", value: Number(this.d.home_prediction) || 0, actual: actual === "home" },
        { key: "draw", label: "Draw", value: Number(this.d.draw_prediction) || 0, actual: actual === "draw" },
        { key: "away", label: "Away", value: Number(this.d.away_prediction) || 0, actual: actual === "away" },
      ];
    },
    // {home: [{name, shots, sot}], away: [...]}, most shots first, from the grouped stat lists
    shooters() {
      const stats = this.d.stats || {};
      const side = (key) => {
        const sot = {};
        for (const g of (stats[key] || {}).shots_on_target || []) {
          for (const name of String(g.players).split(", ")) sot[name] = g.value;
        }
        const out = [];
        for (const g of (stats[key] || {}).shots || []) {
          for (const name of String(g.players).split(", ")) out.push({ name, shots: g.value, sot: sot[name] || 0 });
        }
        return out.sort((a, b) => b.shots - a.shots || b.sot - a.sot).slice(0, 4);
      };
      return { home: side("home_team"), away: side("away_team") };
    },
    booked() {
      const out = { home: [], away: [] };
      const seen = {};
      for (const e of this.d.events || []) {
        if (!CARDS.includes(e.type) || !e.player) continue;
        const home = String(e.team_id) === String(this.d.home_team.id);
        const key = `${home}-${e.player}`;
        const red = e.type !== "yellowcard";
        if (seen[key]) {
          if (red) seen[key].icon = EVENT_ICONS[e.type]; // a later red replaces the yellow
          continue;
        }
        seen[key] = { name: e.player, icon: EVENT_ICONS[e.type] };
        out[home ? "home" : "away"].push(seen[key]);
      }
      return out;
    },
    hasDetail() {
      return this.timeline.length || this.statRows.length || this.hasXg || this.booked.home.length || this.booked.away.length;
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    isFocus(team) {
      return this.teamId !== null && String(team.id) === String(this.teamId);
    },
    fmt1(v) {
      return Number(v || 0).toFixed(1);
    },
  },
};
</script>

<style scoped>
.tint-w {
  background: rgba(34, 197, 94, 0.16);
}
.tint-d {
  background: rgba(156, 163, 175, 0.18);
}
.tint-l {
  background: rgba(255, 105, 105, 0.18);
}
.timeline-row {
  grid-template-columns: minmax(0, 1fr) 3.5rem minmax(0, 1fr);
}
</style>
